import { useRef, useState } from 'react';
import { InfiniteData } from '@tanstack/react-query';
import { NSButton, NSRichTextEditor } from 'bricks';
import { NSRichTextEditorRef } from 'bricks/NSRichTextEditor/NSRichTextEditor';
import { useAuthContext } from 'contexts/AuthContext';
import useToast from 'hooks/useToast';
import { IUser } from 'types/user';
import infiniteOrganizationUsersQueryOptions from 'views/DealDashboard/Activity/hooks/useInfiniteOrganizationUsers';
import { useCreateComment } from 'views/DealDashboard/Overview/Timeline/hooks/useCreateComment';

export interface CommentComposerProps {
    orgId: string;
    dealId: number;
    timelineId: number;
    parentId?: number;
    placeholder?: string;
    onSubmitted?: () => void;
}

const CommentComposer = ({
    orgId, dealId, timelineId, parentId, placeholder = 'Post a comment on this task. Use @ to mention a teammate.', onSubmitted,
}: CommentComposerProps) => {
    const editorRef = useRef<NSRichTextEditorRef>(null);
    const [isEditorEmpty, setIsEditorEmpty] = useState(true);

    const { user: currentUser } = useAuthContext();
    const { showError } = useToast();
    const { mutateAsync: createComment, isPending } = useCreateComment();

    const mentionsQueryOptions = (query: string) => ({
        ...infiniteOrganizationUsersQueryOptions(orgId, { search_value: query }),
        enabled: !!query,
        select: (data: InfiniteData<IUser[]>) => data.pages.flatMap(page => page
            .filter(orgUser => orgUser.id !== currentUser?.id)
            .map(orgUser => ({
                type: 'user',
                name: orgUser.name,
                id: orgUser.id,
                picture: orgUser.picture || '',
                correlationId: orgUser.correlationId,
            }))),
    });

    const handleEditorChange = () => {
        if (editorRef.current) {
            setIsEditorEmpty(editorRef.current.isEditorEmpty());
        }
    };

    const handleSubmit = async () => {
        if (!editorRef.current || isEditorEmpty) {
            return;
        }
        
        const bodyHtml = editorRef.current.getEditorContent().htmlContent;
        const bodyEditorState = editorRef.current.getEditorState()?.toJSON() ?? null;
        const mentions = editorRef.current.getMentionCorrelationIds();

        try {
            await createComment({
                orgId,
                dealId,
                timelineId,
                payload: {
                    bodyHtml, 
                    bodyEditorState, 
                    parentId: parentId ?? null, 
                    mentions,
                },
            });
            editorRef.current.clear();
            setIsEditorEmpty(true);
            onSubmitted?.();
        } catch {
            showError('Failed to post comment. Please try again.');
        }
    };

    return (
        <div data-testid="comment-composer">
            <NSRichTextEditor
                ref={editorRef}
                placeholder={placeholder}
                onChange={handleEditorChange}
                mentionsQueryOptions={mentionsQueryOptions}
            />
            <div className="d-flex justify-content-end">
                <NSButton
                    callback={handleSubmit}
                    color="primary"
                    size="sm"
                    outline={false}
                    disabled={isEditorEmpty}
                    isLoading={isPending}
                >
                    Comment
                </NSButton>
            </div>
        </div>
    );
};

export default CommentComposer;
